import { apiCall } from '../utils/apiUtils';

const BASE = '/api/external/v1/live-stamping';

function normalise(res) {
  if (res && res.success === false && res.error) {
    throw Object.assign(new Error(res.error.message || 'Request failed'), { code: res.error.code });
  }
  return res;
}

export const listServices = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiCall(`${BASE}/services${qs ? `?${qs}` : ''}`, { method: 'GET' }).then(normalise);
};

export const placeOrder = (payload) =>
  apiCall(`${BASE}/request`, { method: 'POST', body: JSON.stringify(payload) }).then(normalise);

export const listOrders = (filters = {}) => {
  const params = { include_base64: 'false' };
  if (filters.status) params.status = filters.status;
  if (filters.from_date) params.from_date = filters.from_date;
  if (filters.to_date) params.to_date = filters.to_date;
  if (filters.external_ref_id) params.external_ref_id = filters.external_ref_id;
  if (filters.source_csv_upload_id) params.source_csv_upload_id = filters.source_csv_upload_id;
  if (filters.search) params.search = filters.search;
  if (filters.service_name) params.service_name = filters.service_name;
  if (filters.client_name_filter) params.client_name_filter = filters.client_name_filter;
  if (filters.limit) params.limit = filters.limit;
  if (filters.offset !== undefined) params.offset = String(filters.offset);
  const qs = new URLSearchParams(params).toString();
  return apiCall(`${BASE}/orders?${qs}`, { method: 'GET' }).then(normalise);
};

export const getOrder = (id) =>
  apiCall(`${BASE}/orders/${id}?include_base64=true`, { method: 'GET' }).then(normalise);

export const getWallet = () =>
  apiCall(`${BASE}/wallet`, { method: 'GET' }).then(normalise);

export const requestDeposit = (amount, reference, notes, payment_date) =>
  apiCall(`${BASE}/wallet/deposit`, {
    method: 'POST',
    body: JSON.stringify({ amount, reference, notes, payment_date })
  }).then(normalise);

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const vendorUploadPreview = async (orderId, previewFile, metadata) => {
  const form = new FormData();
  form.append('preview', previewFile);
  form.append('metadata', JSON.stringify(metadata));
  const res = await fetch(`${BASE_URL}${BASE}/orders/${orderId}/preview`, {
    method: 'POST', credentials: 'include', body: form
  });
  const data = await res.json();
  if (!res.ok) throw Object.assign(new Error(data.detail?.message || data.detail || 'Upload failed'), { code: data.detail?.code });
  return normalise(data);
};

export const vendorUploadFinal = async (orderId, pdfFile) => {
  const form = new FormData();
  form.append('file', pdfFile);
  const res = await fetch(`${BASE_URL}${BASE}/orders/${orderId}/upload`, {
    method: 'POST', credentials: 'include', body: form
  });
  const data = await res.json();
  if (!res.ok) throw Object.assign(new Error(data.detail?.message || data.detail || 'Upload failed'), { code: data.detail?.code });
  return normalise(data);
};

// ── Admin: order verification ─────────────────────────────────────────────────

export const approveMetadata = (orderId) =>
  apiCall(`${BASE}/orders/${orderId}/approve-preview`, { method: 'POST' }).then(normalise);

export const approveFinal = (orderId) =>
  apiCall(`${BASE}/orders/${orderId}/approve-final`, { method: 'POST' }).then(normalise);

export const rejectOrder = (orderId, reason) =>
  apiCall(`${BASE}/orders/${orderId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason })
  }).then(normalise);

// ── Admin: webhook management ────────────────────────────────────────────────

const WH_BASE = '/api';

export const getMyWebhookConfig = () =>
  apiCall(`${WH_BASE}/webhooks/config`, { method: 'GET' }).then(normalise);

export const setMyWebhookConfig = (webhookUrl) =>
  apiCall(`${WH_BASE}/webhooks/config`, {
    method: 'PUT',
    body: JSON.stringify({ webhookUrl })
  }).then(normalise);

export const fireMyWebhookTestEvent = () =>
  apiCall(`${WH_BASE}/webhooks/test-fire`, { method: 'POST' }).then(normalise);

export const toggleWebhook = (tenantId, enabled) =>
  apiCall(`${WH_BASE}/admin/webhooks/toggle?tenantId=${encodeURIComponent(tenantId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled })
  }).then(normalise);

/** Rolling webhook delivery analytics (SUCCESS / FAILED by processedAt UTC). Defaults to last 7 days. */
export const fetchWebhookAnalytics = (days = 7) =>
  apiCall(
    `${WH_BASE}/admin/webhooks/analytics?days=${encodeURIComponent(String(days))}`,
    { method: 'GET' }
  ).then(normalise);

export const listWebhookEvents = (filters = {}) => {
  const params = {};
  if (filters.tenantId) params.tenantId = filters.tenantId;
  if (filters.eventType) params.eventType = filters.eventType;
  if (filters.entityId) params.entityId = filters.entityId;
  if (filters.status) params.status = filters.status;
  if (filters.limit) params.limit = String(filters.limit);
  if (filters.offset !== undefined) params.offset = String(filters.offset);
  const qs = new URLSearchParams(params).toString();
  return apiCall(`${WH_BASE}/admin/webhooks/events${qs ? `?${qs}` : ''}`, { method: 'GET' }).then(normalise);
};

export const replayWebhookEvent = (eventId) =>
  apiCall(`${WH_BASE}/admin/webhooks/events/${eventId}/replay`, { method: 'POST' }).then(normalise);

export const testFireWebhook = (tenantId, eventType) =>
  apiCall(`${WH_BASE}/admin/webhooks/test-fire`, {
    method: 'POST',
    body: JSON.stringify({ tenantId, eventType })
  }).then(normalise);

/** Full admin test-fire body (matches backend TestFireRequest). */
export const adminTestFireWebhook = (body) =>
  apiCall(`${WH_BASE}/admin/webhooks/test-fire`, {
    method: 'POST',
    body: JSON.stringify(body),
  }).then(normalise);

/** Public RSA keys for webhook signature verification (no auth). */
export const fetchWebhookPublicKeys = () =>
  apiCall(`${WH_BASE}/public-keys`, { method: 'GET' }).then(normalise);
